/**
 * Markdown rendering for paste content, which is untrusted by definition.
 * Two independent layers: markdown-it runs with `html: false` so raw HTML in
 * the source is escaped rather than parsed, and DOMPurify sanitises the result
 * against an explicit allowlist.
 *
 * ```mermaid fences are deliberately left as ordinary code blocks here;
 * diagrams.ts swaps them for rendered SVG afterwards, and a paste that never
 * uses one never loads mermaid.
 */

const ALLOWED_TAGS = [
  'p', 'br', 'hr', 'span',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'em', 'del', 's', 'sup', 'sub', 'kbd',
  'code', 'pre', 'blockquote',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'a', 'img',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
]

const ALLOWED_ATTR = ['href', 'title', 'alt', 'src', 'class', 'colspan', 'rowspan', 'align', 'start']

type Renderer = (source: string) => string

let loading: Promise<Renderer> | null = null

function load(): Promise<Renderer> {
  loading ??= (async () => {
    const [{ default: MarkdownIt }, { default: DOMPurify }] = await Promise.all([
      import('markdown-it'),
      import('dompurify'),
    ])

    const md = new MarkdownIt({ html: false, linkify: true, breaks: false })

    // Paste content is someone else's text: never send a referrer, never let a
    // link take over this tab.
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      if (node.nodeName === 'A') {
        node.setAttribute('target', '_blank')
        node.setAttribute('rel', 'noopener noreferrer nofollow ugc')
      }
    })

    return (source: string) =>
      DOMPurify.sanitize(md.render(source), {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        ALLOW_DATA_ATTR: false,
      })
  })()
  return loading
}

export async function renderMarkdown(source: string): Promise<string> {
  const render = await load()
  return render(source)
}
